import { ASSET_VERSIONS } from './versions.gen'

/**
 * Every game file's URL — this origin in development, the asset CDN in production.
 *
 * Game files live in `public/games/<game-id>/…` and `public/shared/…`. With
 * `VITE_ASSET_BASE_URL` unset (dev, tests, and any build that has not moved to
 * the CDN yet) a path comes back exactly as written and Vite or Pages serves it.
 * Set — `https://assets.kalqy.in` — the same path becomes
 * `https://assets.kalqy.in/games/sky-quest/<version>/models/bird/bird.glb`.
 *
 * 🔴 THE VERSION IS THE BUNDLE'S CONTENT HASH (`versions.gen.ts`). The CDN
 * serves every object `immutable` for a year, so a changed file must be a
 * changed URL or no school device would ever see it. One game's art changing
 * moves only that game's URLs; the other four stay cached.
 *
 * ⚠️ EVERY `/games/…` AND `/shared/…` PATH GOES THROUGH HERE. A literal that
 * skips it works in dev and 404s on the CDN, where the unversioned path was
 * never uploaded — `npm run assets:paths:check` fails the build on one.
 */
export type AssetPath = `/games/${string}` | `/shared/${string}`

const BUNDLE = /^\/(games\/[a-z0-9-]+|shared)(?=\/|$)(.*)$/

/** The pure half of `assetUrl`, so it can be tested without a build-time env. */
export function resolveAssetUrl(path: AssetPath, base: string, versions: Readonly<Record<string, string>>): string {
  const root = base.replace(/\/+$/, '')
  if (!root) return path
  const match = BUNDLE.exec(path)
  const version = match?.[1] ? versions[match[1]] : undefined
  if (!match?.[1] || !version) return `${root}${path}`
  return `${root}/${match[1]}/${version}${match[2] ?? ''}`
}

const BASE: string = import.meta.env.VITE_ASSET_BASE_URL ?? ''

export function assetUrl(path: AssetPath): string {
  return resolveAssetUrl(path, BASE, ASSET_VERSIONS)
}
